import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { generateBrandedProductSlug } from '../utils/slugUtils';

// Container component
const Container = ({ children, className = "" }) => (
    <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SimilarProducts = ({ 
    products = [], 
    currentProductId = null,
    title = "Shop Similar Products",
    subtitle = "Explore more products you might love"
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    
    // Filter out current product if on product detail page
    const filteredProducts = useMemo(() => {
        let filtered = products.filter(p => p.isActive);
        if (currentProductId) {
            filtered = filtered.filter(p => p._id !== currentProductId);
        }
        // Limit to 12 products for the carousel
        return filtered.slice(0, 12);
    }, [products, currentProductId]);

    // Calculate slides (4 products per slide on desktop, 1 on mobile, 2 on tablet)
    const slidesPerView = {
        mobile: 1,
        tablet: 2,
        desktop: 4
    };

    const totalSlides = Math.ceil(filteredProducts.length / slidesPerView.desktop);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const stars = [];

        for (let i = 0; i < fullStars; i++) {
            stars.push(<Star key={`full-${i}`} className="h-4 w-4 fill-[#ab3d22] text-[#ab3d22]" />);
        }
        if (hasHalfStar) {
            stars.push(<Star key="half" className="h-4 w-4 text-[#ab3d22]" />);
        }
        const remainingStars = 5 - Math.ceil(rating);
        for (let i = 0; i < remainingStars; i++) {
            stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
        }
        return stars;
    };

    if (filteredProducts.length === 0) {
        return null; // Don't show section if no products
    }

    return (
        <section className="bg-white py-12 sm:py-16">
            <Container>
                {/* Section Header */}
                <div className="text-center mb-8 sm:mb-12">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                        {title}
                    </h2>
                    <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                        {subtitle}
                    </p>
                </div>

                {/* Carousel */}
                <div className="relative">
                    {/* Previous Button */}
                    {totalSlides > 1 && (
                        <button
                            onClick={prevSlide}
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-[#ab3d22] hover:text-white hidden md:flex items-center justify-center"
                            aria-label="Previous products"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                    )}

                    {/* Products Container */}
                    <div className="overflow-hidden">
                        <div 
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                        >
                            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                                <div 
                                    key={slideIndex} 
                                    className="w-full flex-shrink-0"
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                                        {filteredProducts
                                            .slice(slideIndex * slidesPerView.desktop, (slideIndex + 1) * slidesPerView.desktop)
                                            .map((product) => (
                                                <Link
                                                    key={product._id}
                                                    to={`/product/${generateBrandedProductSlug(product.brand, product.name)}`}
                                                    className="group block bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all duration-300 hover:border-[#ab3d22]"
                                                >
                                                    {/* Product Image */}
                                                    <div className="relative mb-4 bg-white rounded-xl p-3">
                                                        <img
                                                            src={product.imageUrl}
                                                            alt={product.name}
                                                            className="w-full h-48 object-contain"
                                                        />
                                                        {/* Badges */}
                                                        {product.badges && product.badges.length > 0 && (
                                                            <div className="absolute top-2 right-2">
                                                                <span className="inline-block bg-[#ab3d22] text-white text-xs font-medium px-2 py-1 rounded-full">
                                                                    {product.badges[0].replace(/_/g, ' ')}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Brand */}
                                                    <div className="mb-1">
                                                        <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                                                            {product.brand}
                                                        </span>
                                                    </div>

                                                    {/* Product Name */}
                                                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-[#ab3d22] transition-colors min-h-[2.5rem]">
                                                        {product.name}
                                                    </h3>

                                                    {/* Rating */}
                                                    <div className="flex items-center gap-1 mb-3">
                                                        <div className="flex gap-0.5">
                                                            {renderStars(product.rating || 0)}
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-900 ml-1">
                                                            {(product.rating || 0).toFixed(1)}
                                                        </span>
                                                    </div>

                                                    {/* CTA */}
                                                    <div className="pt-3 border-t border-gray-100">
                                                        <span className="text-sm font-medium text-[#ab3d22] group-hover:underline">
                                                            View Product →
                                                        </span>
                                                    </div>
                                                </Link>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Next Button */}
                    {totalSlides > 1 && (
                        <button
                            onClick={nextSlide}
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-[#ab3d22] hover:text-white hidden md:flex items-center justify-center"
                            aria-label="Next products"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    )}
                </div>

                {/* Dots Indicator - Mobile */}
                {totalSlides > 1 && (
                    <div className="flex justify-center gap-2 mt-6 md:hidden">
                        {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    currentSlide === index 
                                        ? 'w-8 bg-[#ab3d22]' 
                                        : 'w-2 bg-gray-300'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                )}

                {/* Dots Indicator - Desktop */}
                {totalSlides > 1 && (
                    <div className="hidden md:flex justify-center gap-2 mt-8">
                        {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    currentSlide === index 
                                        ? 'w-8 bg-[#ab3d22]' 
                                        : 'w-2 bg-gray-300'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </Container>
        </section>
    );
};

export default SimilarProducts;

